import { useState, useEffect, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Button,
  useDisclosure,
  VStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  FormControl,
  FormLabel,
  Icon,
  Tooltip,
  Stack,
  Skeleton,
  VisuallyHidden,
} from "@chakra-ui/react";
import { FiCalendar, FiFileText, FiRotateCcw } from "react-icons/fi";
import { useQuery } from "@tanstack/react-query";
import { parseDate } from "../utils/parseDate";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import {
  ContractRow,
  DataTable,
  EmptyState,
  FilterButton,
  Meter,
  MotionReveal,
  PageHeader,
  Pagination,
  SearchField,
  SectionCard,
} from "../components/ui";
import CreditApplications from "../components/creditApplications";

interface ContractsPageProps {}

type contractsData = {
  name: {
    value: string;
    text: any;
  };
  pl: {
    value: string;
    text: string;
  };
  customer: {
    value: string;
    text: string;
  };
  startDate: {
    value: string;
    text: any;
  };
  endDate: {
    value: string;
    text: any;
  };
  items: contractItems;
};

type contractItems = Array<{
  item: {
    value: string;
    text: any;
  };
  itemDescription?: {
    value: string;
    text: any;
  };
  contractVolume: {
    value: string;
    text: any;
  };
  totalConsumption: {
    value: string;
    text: any;
  };
  consumptionLATotal: {
    value: string;
    text: any;
  };
  consumptionLTD: {
    value: string;
    text: any;
  };
  monthlyContactVolume: {
    value: string;
    text: any;
  };
  salesTotal: {
    value: string;
    text: any;
  };
}>;

type ContractFilters = {
  startDate: string;
  endDate: string;
  search: string;
};

type FilterKey = "startDate" | "endDate" | "search";

const ITEM_COLUMNS = ["Item", "Description", "Volume", "Consumed", "Last month", "This month", "Monthly"];

/** API numbers arrive as strings, sometimes with thousands separators. */
const toNumber = (raw: unknown): number => {
  const n = parseFloat(String(raw ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : NaN;
};

const pluralise = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/**
 * Date-range filter popover (§8.12). Hoisted out of the page render body so its open state survives re-renders;
 * the filters state and handlers still live in the page.
 */
const FilterPanel = ({
  filters,
  resultCount,
  handleFilterChange,
  onReset,
}: {
  filters: ContractFilters;
  resultCount: number;
  handleFilterChange: (type: FilterKey, value: string) => void;
  onReset: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Only the keys this popover owns — the search lives in its own field beside the button.
  const activeCount = [filters.startDate, filters.endDate].filter(Boolean).length;

  return (
    <Popover
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      strategy="fixed"
      closeOnBlur={false}
    >
      <PopoverTrigger>
        <FilterButton
          activeCount={activeCount}
          isOpen={isOpen}
          onClick={() => setIsOpen(!isOpen)}
        />
      </PopoverTrigger>

      {/* Rendered in place (not portalled) so Tab order follows the trigger; strategy="fixed" lets it escape the band's overflow. */}
      <PopoverContent w="320px" maxW="calc(100vw - 2rem)">
        <PopoverHeader>
          <Text textStyle="label" color="text.muted">
            Contract period
          </Text>
        </PopoverHeader>
        <PopoverBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel htmlFor="contracts-filter-start">Start date</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiCalendar} boxSize="16px" color="sand.500" aria-hidden />
                </InputLeftElement>
                <Input
                  id="contracts-filter-start"
                  type="date"
                  pl={10}
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </InputGroup>
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="contracts-filter-end">End date</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiCalendar} boxSize="16px" color="sand.500" aria-hidden />
                </InputLeftElement>
                <Input
                  id="contracts-filter-end"
                  type="date"
                  pl={10}
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </InputGroup>
            </FormControl>
          </VStack>
        </PopoverBody>
        <PopoverFooter display="flex" justifyContent="space-between" alignItems="center">
          <Text textStyle="caption" color="text.muted" aria-live="polite">
            {pluralise(resultCount, "result")}
          </Text>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<FiRotateCcw />}
            onClick={onReset}
          >
            Reset
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

/** One line of the nested items table: value + a small consumption meter where the contract volume is known. */
const ContractItemRow = ({ item }: { item: contractItems[number] }) => {
  const description = item.itemDescription?.value ?? "";
  // Only descriptions long enough to be clipped at 28ch get the tooltip — and a tab stop so keyboard users can open it.
  const mayTruncate = description.length > 28;
  const volume = toNumber(item.contractVolume?.value);
  const consumed = toNumber(item.totalConsumption?.value);
  const showMeter = volume > 0 && Number.isFinite(consumed);
  const pct = showMeter ? Math.round((consumed / volume) * 100) : 0;

  return (
    <Tr>
      <Td>
        <Text as="span" fontWeight={600} color="text.primary">
          {item.item?.value}
        </Text>
      </Td>
      <Td>
        <Tooltip label={description} hasArrow openDelay={300} isDisabled={!mayTruncate}>
          <Text
            noOfLines={1}
            maxW="28ch"
            color="text.secondary"
            tabIndex={mayTruncate ? 0 : undefined}
            borderRadius="sm"
            _focusVisible={{ boxShadow: "outline", outline: "none" }}
          >
            {description}
          </Text>
        </Tooltip>
      </Td>
      <Td isNumeric>{item.contractVolume?.value}</Td>
      <Td isNumeric>
        <Flex justify="flex-end" align="center" gap={2}>
          <Text as="span" textStyle="numeral">
            {item.totalConsumption?.value}
          </Text>
          {showMeter && (
            <Meter
              value={consumed}
              max={volume}
              tone="auto"
              w="56px"
              label={`${pct}% of contract volume consumed`}
            />
          )}
        </Flex>
      </Td>
      <Td isNumeric>{item.consumptionLATotal?.value}</Td>
      <Td isNumeric>{item.consumptionLTD?.value}</Td>
      <Td isNumeric>{item.monthlyContactVolume?.value}</Td>
    </Tr>
  );
};

/**
 * Loading state in the real list structure (§7.0): ContractRow-shaped items — icon, name + period bars, P&L and
 * date bars from md, and the items pill — with the same padding and hairline dividers, so nothing jumps on load.
 */
const SKELETON_NAME_WIDTHS = ["55%", "70%", "45%", "62%", "50%"];

const ContractListSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <Box aria-busy="true">
    <VisuallyHidden>Loading</VisuallyHidden>
    <Box as="ul" listStyleType="none" m={0} p={0} aria-hidden>
      {Array.from({ length: rows }).map((_, index) => (
        <Box
          as="li"
          key={index}
          px={4}
          py={4}
          borderBottom="1px solid"
          borderColor="border.default"
          _last={{ borderBottom: "none" }}
        >
          <Box
            display="grid"
            gridTemplateColumns={{ base: "auto 1fr auto", md: "auto 2fr 1fr 1.4fr auto" }}
            alignItems="center"
            columnGap={4}
          >
            <Skeleton speed={1.2} w="40px" h="40px" borderRadius="md" />
            <Box minW={0}>
              <Skeleton speed={1.2} h="1rem" w={SKELETON_NAME_WIDTHS[index % SKELETON_NAME_WIDTHS.length]} mb={2} />
              <Skeleton speed={1.2} h="0.625rem" w="40%" />
            </Box>
            <Skeleton speed={1.2} h="1.25rem" w="4.5rem" borderRadius="full" display={{ base: "none", md: "block" }} />
            <Skeleton speed={1.2} h="0.875rem" w="80%" display={{ base: "none", md: "block" }} />
            <Skeleton speed={1.2} h="2rem" w="5.5rem" borderRadius="full" />
          </Box>
        </Box>
      ))}
    </Box>
  </Box>
);

const ContractsPage: React.FC<ContractsPageProps> = () => {
  const token = getAuthToken();
  const { isOpen, onToggle } = useDisclosure();
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    search: ""  // Add search to filters
  });

  // get contracts list
  const getContracts = async () => {
    const { data } = await axiosClient.get("/contracts/listContract", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data.data;
  };

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ["contracts"],
    queryFn: getContracts,
  });

  // pagination
  const [page, setPage] = useState(1);
  const [filteredContracts, setFilteredContracts] = useState<contractsData[]>(
    []
  );
  const itemsPerPage = 8;

  // Effect to update filteredContracts when data is fetched
  useEffect(() => {
    if (data) {
      setFilteredContracts(data); // Set filteredContracts to the fetched data when available
    }
  }, [data]);

  // Filter logic
  const filteredContractsData = data?.filter((contract: contractsData) => {

    // Date filtering
    const contractStartDate = parseDate(contract.startDate.value);
    const contractEndDate = parseDate(contract.endDate.value);
    const matchesStartDate = filters.startDate
      ? contractStartDate >= new Date(filters.startDate)
      : true;
    const matchesEndDate = filters.endDate
      ? contractEndDate <= new Date(filters.endDate)
      : true;

    const matchesSearch = filters.search
      ? contract.name.value.toLowerCase().includes(filters.search.toLowerCase()) ||
        contract.pl.value.toLowerCase().includes(filters.search.toLowerCase()) ||
        contract.customer.value.toLowerCase().includes(filters.search.toLowerCase()) ||
        contract.startDate.value.toLowerCase().includes(filters.search.toLowerCase()) ||
        contract.endDate.value.toLowerCase().includes(filters.search.toLowerCase())
      : true;

    return matchesStartDate && matchesEndDate && matchesSearch;
  });

  // Pagination logic — clamp so a shrinking list (e.g. a background refetch) never leaves the view on an empty page
  const pageCount = Math.max(1, Math.ceil((filteredContractsData?.length ?? 0) / itemsPerPage));
  const currentPage = Math.min(page, pageCount);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = filteredContractsData?.slice(
    indexOfFirstItem,
    indexOfLastItem || filteredContractsData?.length || 0
  );
  const handleFilterChange = (
    type: "startDate" | "endDate" | "search",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [type]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      search: ""
    });
    setPage(1);
  };

  // Presentation-only derived values
  const isFirstLoad = isPending && !filteredContracts.length;
  const rows: contractsData[] = currentItems ?? [];
  const resultCount: number = filteredContractsData?.length ?? 0;
  const totalCount: number = filteredContracts.length;
  const hasActiveFilters = Object.values(filters).some((v) => v);
  const hasDateFilter = Boolean(filters.startDate || filters.endDate);
  const searchOnly = Boolean(filters.search) && !hasDateFilter;
  // The empty-state reset button unmounts itself once rows reappear — hand focus back to the search field.
  const searchRef = useRef<HTMLInputElement>(null);
  const resetFromEmptyState = () => {
    resetFilters();
    searchRef.current?.focus();
  };

  return (
    <Stack spacing={6}>
      <PageHeader
        variant="strip"
        eyebrow="CONTRACTS"
        title="My contracts"
        actions={
          <>
            <SearchField
              ref={searchRef}
              value={filters.search}
              onChange={(value) => handleFilterChange("search", value)}
              placeholder="Search contracts"
              aria-label="Search contracts"
            />
            <FilterPanel
              filters={filters}
              resultCount={resultCount}
              handleFilterChange={handleFilterChange}
              onReset={resetFilters}
            />
          </>
        }
      />

      <MotionReveal delay={0.08}>
        <SectionCard
          folio="01"
          title="Agreements"
          lede={
            isFirstLoad || (isError && !data)
              ? undefined
              : `${
                  hasActiveFilters ? `${resultCount} of ${totalCount} contracts` : pluralise(resultCount, "contract")
                } · consumption updates daily`
          }
          isLoading={isFirstLoad}
        >
          {isFirstLoad ? (
            <ContractListSkeleton rows={5} />
          ) : isError && !data ? (
            <Alert status="error" alignItems="flex-start">
              <AlertIcon />
              <Box flex="1">
                <AlertTitle>We couldn't load your contracts</AlertTitle>
                <AlertDescription>Check your connection and try again.</AlertDescription>
              </Box>
              <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          ) : resultCount === 0 ? (
            hasActiveFilters ? (
              <EmptyState
                icon={FiFileText}
                title={searchOnly ? `No contracts match "${filters.search}"` : "No contracts match these filters"}
                description={
                  searchOnly
                    ? "Try another name, P&L or date."
                    : filters.search
                    ? "Try widening the date range or clearing the search."
                    : "Try widening the date range."
                }
                action={
                  <Button variant="secondary" size="sm" leftIcon={<FiRotateCcw />} onClick={resetFromEmptyState}>
                    {searchOnly ? "Clear search" : "Reset filters"}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={FiFileText}
                title="No contracts yet"
                description="Your agreements with Bidfood will appear here once they are set up."
              />
            )
          ) : (
            <Box as="ul" listStyleType="none" m={0} p={0} aria-label="Contracts">
              {rows.map((contract, index) => (
                <Box
                  as="li"
                  key={`${contract.name.value}-${indexOfFirstItem + index}`}
                  borderBottom="1px solid"
                  borderColor="border.default"
                  _last={{ borderBottom: "none" }}
                >
                  <ContractRow
                    name={contract.name.value}
                    pnl={contract.pl.text}
                    startDate={parseDate(contract.startDate.value)}
                    endDate={parseDate(contract.endDate.value)}
                    isOpen={index === selectedRow ? isOpen : false}
                    onToggle={() => {
                      setSelectedRow(index);
                      onToggle();
                    }}
                    itemCount={contract.items?.length ?? 0}
                    panelId={`contract-${indexOfFirstItem + index}`}
                  >
                    {contract.items?.length ? (
                      <DataTable size="sm" label={`Items in ${contract.name.value}`}>
                        <Table>
                          <Thead>
                            <Tr>
                              {ITEM_COLUMNS.map((column, columnIndex) => (
                                <Th key={column} scope="col" isNumeric={columnIndex >= 2}>
                                  {column}
                                </Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {contract.items.map((item, itemIndex) => (
                              <ContractItemRow key={`${item.item?.value}-${itemIndex}`} item={item} />
                            ))}
                          </Tbody>
                        </Table>
                      </DataTable>
                    ) : (
                      <Text textStyle="body.sm" color="text.muted" py={2}>
                        No items on this agreement yet.
                      </Text>
                    )}
                  </ContractRow>
                </Box>
              ))}
            </Box>
          )}

          {!isFirstLoad && resultCount > 0 && (
            <Pagination
              page={currentPage}
              pageSize={itemsPerPage}
              total={resultCount}
              onChange={setPage}
              itemLabel="contracts"
            />
          )}
        </SectionCard>
      </MotionReveal>

      <MotionReveal delay={0.12}>
        <CreditApplications folio="02" />
      </MotionReveal>
    </Stack>
  );
};

export default ContractsPage;
